import { createTeamRequestSchema } from "@stb/api-contract"
import { createServerFn } from "@tanstack/react-start"

import { isKnownRole } from "@/app/teams/team-nav"
import type { TeamsShell } from "@/app/teams/types"
import { getSessionUser } from "@/auth/session.server"
import { isAppError } from "@/core/app-error"
import { getMainDb } from "@/db/main/db.server"
import { parseFormData } from "@/form"
import { getOrCreateUserOrganisation } from "@/organisations/organisations.server"
import { createTeam, isTeamsEnabled, requireTeamsEnabled } from "@/organisations/teams.server"
import { listUserOrganisations } from "@/trips/me-organisations.server"

// logger.server is imported inside the handlers: a top-level use would keep it in the client bundle
const getLog = async () =>
  (await import("@/core/logger.server")).logger.child({ module: "teams-shell" })
const OFF: TeamsShell = { enabled: false, organisations: [] }

/**
 * What the shell needs for the organisation switcher: the same list `GET /api/v1/me` returns as
 * `organisations[]` (ADR-017 2.2), empty while TEAMS_ENABLED is off. A failed lookup turns Teams
 * off for this page load instead of breaking the whole app shell.
 */
export const getTeamsShell = createServerFn({ method: "GET" }).handler(
  async (): Promise<TeamsShell> => {
    if (!isTeamsEnabled()) return OFF
    const user = await getSessionUser()
    if (!user) return OFF
    try {
      const db = getMainDb()
      // like /me: the personal organisation is created on first use, so a new user still gets a switcher
      await getOrCreateUserOrganisation(db, user.id)
      const entries = await listUserOrganisations(db, user.id)
      return {
        enabled: true,
        // a role, kind or status this client does not know is skipped, never an error
        organisations: entries.flatMap((e) =>
          e.status === "active" &&
          (e.kind === "personal" || e.kind === "team") &&
          isKnownRole(e.role)
            ? [{ id: e.id, name: e.name, kind: e.kind, role: e.role }]
            : [],
        ),
      }
    } catch (error) {
      ;(await getLog()).error("teams shell lookup failed", {
        operation: "get_teams_shell",
        user_id: user.id,
        error,
      })
      return OFF
    }
  },
)

/** Same behaviour as POST /api/v1/teams (switch check, strict name schema), for the web session. */
export const createTeamFormAction = createServerFn({ method: "POST" })
  .validator((data: FormData) => parseFormData(createTeamRequestSchema, data))
  .handler(async ({ data }): Promise<{ id: string }> => {
    const user = await getSessionUser()
    if (!user) throw new Error("Sign in to continue.")
    try {
      requireTeamsEnabled()
      const team = await createTeam(getMainDb(), user.id, data.name)
      return { id: team.id }
    } catch (error) {
      if (isAppError(error)) throw new Error(error.message, { cause: error })
      ;(await getLog()).error("team creation failed", {
        operation: "create_team_web",
        user_id: user.id,
        error,
      })
      throw new Error("Something went wrong. Please try again.", { cause: error })
    }
  })
